import { useEffect, useRef, useState } from 'react';
import {
  V2Alert as Alert,
  LoadingState,
  V2Badge,
  V2Button,
  V2Card,
  V2CardHeader,
  V2Table,
  V2Modal as Modal,
  V2Kpi,
  V2MetricStrip,
  V2Tabs,
  V2EmptyState,
  type V2TableColumn,
} from '@sakhtar/design-system/react';
import { apiGet, apiPost, ApiError } from '../../lib/apiClient.js';
import {
  importDestinationPath,
  type ImportDestination,
} from '../../lib/dataImport/openingImport.js';
import { triggerBlobDownload } from '../../lib/dataImport/xlsxWorkbook.js';
import {
  buildGuidedImportTemplate,
  type GuidedImportPack,
} from '../../lib/dataImport/guidedImportTemplate.js';

import { buildImportReview, relationConfidence } from '../../lib/dataImport/guidedImportReview.js';
import {
  importAnalysisPresentation,
  type ImportAnalysis,
} from '../../lib/dataImport/guidedImportAnalysis.js';
import { GuidedImportEditor, type EditorSelection } from './GuidedImportEditor.js';
import { ImportPackCard, importPackModule } from './ImportPackCard.js';
import { ImportGuide } from './ImportGuide.js';
import {
  GuidedRelationReview,
  type RelationDecisionRequest,
  type RelationSaveProgress,
} from './GuidedRelationReview.js';
import { buildRelationReviewGroups } from '../../lib/dataImport/relationReviewGroups.js';
import './guidedImportReview.css';

interface StagedRow {
  rowId: string;
  packId: string;
  sheetId: string;
  sourceRow: number;
  values: Record<string, unknown>;
  errors: readonly (string | { field?: string; message: string })[];
}
interface Relation {
  proposalId: string;
  leftRowId: string;
  rightRowId: string;
  probability: number | null;
  confidence: number | string | null;
  reason: string;
  source?: 'identity' | 'smart' | 'manual';
  decision: 'pending' | 'link' | 'separate';
  strongConflict: boolean;
}
interface Workspace {
  workspaceId: string;
  revision: number;
  status: 'staged' | 'review' | 'complete';
  rows: readonly StagedRow[];
  proposals: readonly Relation[];
  files?: readonly { packId: string; fileName: string }[];
  summary?: Record<string, unknown>;
  groups?: readonly { canonicalRowId: string; rowIds: readonly string[] }[];
  referenceLinks?: readonly {
    fromRowId: string;
    toRowId: string;
    sourceTargetRowId?: string;
    label: string;
  }[];
  analysisNotice?: string | null;
  analysis?: ImportAnalysis | null;
  previewToken?: string | null;
  result?: Outcome | null;
}
interface Preview extends Workspace {
  previewToken: string;
  errorCount: number;
  warningCount: number;
  committableCount: number;
  blockedPacks: readonly { packId: string; reason: string }[];
  diagnostics?: readonly { code: string; message: string }[];
}
interface Outcome {
  retainedSettledInvoiceCount?: number;
  committedCount?: number;
  skippedCount?: number;
  replayed?: boolean;
  financialStatus?: string | null;
  opening?: { batchId?: string; itemCount: number; status: 'draft' } | null;
  destinations?: readonly ImportDestination[];
  orders?: readonly { code: string; sourceCode: string }[];
  blockedPacks?: readonly { packId: string; reason: string }[];
}
const MAX_FILE_BYTES = 5 * 1024 * 1024;
const MAX_TOTAL_BYTES = 10 * 1024 * 1024;
const steps = ['انتخاب فایل‌ها', 'بررسی اطلاعات', 'بررسی ارتباط‌ها و تأیید', 'نتیجه'];
const errorMessage = (error: unknown) =>
  error instanceof ApiError &&
  error.status === 409 &&
  error.envelope.error.details?.some((detail) => detail.code === 'IMPORT_REVISION_CONFLICT')
    ? 'اطلاعات این ورود در درخواست دیگری تغییر کرده است. آخرین وضعیت را دریافت و اصلاحات را دوباره بررسی کنید.'
    : error instanceof ApiError && error.status === 403
      ? 'برای این عملیات دسترسی ندارید.'
      : error instanceof Error
        ? error.message
        : 'عملیات انجام نشد؛ دوباره تلاش کنید.';
async function encodeFile(file: File) {
  const bytes = new Uint8Array(await file.arrayBuffer());
  let binary = '';
  for (let i = 0; i < bytes.length; i += 0x8000)
    binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(binary);
}

/**
 * UI_REFERENCE_SOURCE: Sakhtar Cloud ERP v2 — inventory data-grid, master-data cards and V2 comparison dialogs.
 * Five optional packs share one review workspace; only server-ready data can be committed.
 */
export function GuidedImportView({
  slug,
  base,
  onBack,
}: {
  readonly slug: string;
  readonly base: string;
  readonly onBack: () => void;
}) {
  const [packs, setPacks] = useState<readonly GuidedImportPack[]>([]);
  const [maxRows, setMaxRows] = useState(500);
  const [editor, setEditor] = useState<EditorSelection | null>(null);
  const [saveProgress, setSaveProgress] = useState<{ done: number; total: number } | null>(null);
  const [confirmRevalidation, setConfirmRevalidation] = useState(false);
  const [activePack, setActivePack] = useState('all');
  const [panel, setPanel] = useState('data');
  const [sheetId, setSheetId] = useState('');
  const [query, setQuery] = useState('');
  const [onlyErrors, setOnlyErrors] = useState(false);
  const [integrationPage, setIntegrationPage] = useState(0);
  const [relationProgress, setRelationProgress] = useState<RelationSaveProgress | null>(null);
  const [analysisRunning, setAnalysisRunning] = useState(false);
  const stopOperation = useRef(false);
  const [relationId, setRelationId] = useState<string | null>(null);
  const [sourceId, setSourceId] = useState<string | null>(null);
  const [reviewedPacks, setReviewedPacks] = useState<readonly string[]>([]);
  const [loading, setLoading] = useState(true);
  const [files, setFiles] = useState<Record<string, File>>({});
  const [busy, setBusy] = useState(false);
  const [step, setStep] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [workspace, setWorkspace] = useState<Workspace | null>(null);
  const [rowPage, setRowPage] = useState(0);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  const [canonical, setCanonical] = useState<Record<string, string>>({});
  const [acknowledged, setAcknowledged] = useState(false);
  const [analysisNotice, setAnalysisNotice] = useState<string | null>(null);
  const generation = useRef(0);
  const requestController = useRef<AbortController | null>(null);
  const locked = useRef(false);
  const key = useRef<string | null>(null);
  const path = `${base}/api/v1/import/workspaces`;
  useEffect(() => {
    const current = ++generation.current;
    const controller = new AbortController();
    requestController.current = controller;
    locked.current = false;
    setBusy(false);
    setLoading(true);
    setFiles({});
    setEditor(null);
    setSaveProgress(null);
    setConfirmRevalidation(false);
    setAnalysisNotice(null);
    setWorkspace(null);
    setRowPage(0);
    setReviewedPacks([]);
    setActivePack('all');
    setPanel('data');
    setSheetId('');
    setQuery('');
    setRelationId(null);
    setRelationProgress(null);
    setAnalysisRunning(false);
    stopOperation.current = true;
    setSourceId(null);
    setPreview(null);
    setOutcome(null);
    setStep(0);
    setError(null);
    key.current = null;
    void apiGet<{ items: GuidedImportPack[]; maxRows?: number }>(`${path}/packs`, {
      signal: controller.signal,
    })
      .then((result) => {
        if (generation.current === current) {
          setPacks(result.items);
          setMaxRows(result.maxRows ?? 500);
        }
        const workspaceId = new URLSearchParams(window.location.search).get('workspace');
        if (!workspaceId || generation.current !== current) return;
        return apiGet<Workspace>(`${path}/${encodeURIComponent(workspaceId)}`, {
          signal: controller.signal,
        }).then((restored) => {
          if (generation.current !== current) return;
          setWorkspace(restored);
          setAnalysisNotice(restored.analysisNotice ?? null);
          if (restored.status === 'complete') {
            if (restored.result) {
              setOutcome(restored.result);
              setStep(3);
            } else {
              // A completed workspace must never be offered for a second commit.
              setError(
                'این ورود قبلاً تکمیل شده، اما نتیجهٔ ثبت در دسترس نیست. صفحه را دوباره باز کنید.',
              );
              setStep(3);
            }
          } else {
            setStep(2);
          }
        });
      })
      .catch((cause) => {
        if (generation.current === current) setError(errorMessage(cause));
      })
      .finally(() => {
        if (generation.current === current) setLoading(false);
      });
    return () => {
      controller.abort();
      generation.current += 1;
    };
  }, [path, slug]);
  const label = (id: string) => packs.find((pack) => pack.id === id)?.label ?? id;
  function choose(packId: string, file: File | null) {
    if (locked.current) return;
    setError(null);
    if (file && (!/\.xlsx$/i.test(file.name) || file.size > MAX_FILE_BYTES || file.size === 0)) {
      const cleared = { ...files };
      delete cleared[packId];
      setFiles(cleared);
      key.current = null;
      setError('فقط فایل Excel با پسوند .xlsx و حجم حداکثر ۵ مگابایت انتخاب کنید.');
      return;
    }
    const next = { ...files };
    delete next[packId];
    if (file) next[packId] = file;
    if (Object.values(next).reduce((sum, item) => sum + item.size, 0) > MAX_TOTAL_BYTES) {
      setError('مجموع فایل‌ها باید حداکثر ۱۰ مگابایت باشد.');
      return;
    }
    setFiles(next);
    key.current = null;
    setWorkspace(null);
    setPreview(null);
    setOutcome(null);
    setCanonical({});
    setAcknowledged(false);
  }
  async function perform(action: () => Promise<void>) {
    if (locked.current) return false;
    locked.current = true;
    setBusy(true);
    setError(null);
    const current = generation.current;
    try {
      await action();
      return true;
    } catch (cause) {
      if (generation.current === current) setError(errorMessage(cause));
      return false;
    } finally {
      if (generation.current === current) {
        locked.current = false;
        setBusy(false);
      }
    }
  }
  function start() {
    void perform(async () => {
      if (!Object.keys(files).length) return;
      const current = generation.current;
      key.current ??= crypto.randomUUID();
      const payload = await Promise.all(
        Object.entries(files).map(async ([packId, file]) => ({
          packId,
          fileName: file.name,
          contentBase64: await encodeFile(file),
        })),
      );
      const created = await apiPost<Workspace>(
        path,
        { files: payload },
        {
          headers: { 'idempotency-key': key.current },
          signal: requestController.current?.signal ?? null,
        },
      );
      if (generation.current !== current) return;
      setWorkspace(created);
      const resumeUrl = new URL(window.location.href);
      resumeUrl.searchParams.set('workspace', created.workspaceId);
      window.history.replaceState(null, '', resumeUrl);
      setStep(1);
      const analyzed = await apiPost<Workspace>(`${path}/${created.workspaceId}/analyze`);
      if (generation.current !== current) return;
      setAnalysisNotice(analyzed.analysisNotice ?? null);
      setWorkspace(analyzed);
      setStep(2);
    });
  }
  function decide(proposal: Relation, decision: 'link' | 'separate') {
    void perform(async () => {
      if (
        !workspace ||
        workspace.status !== 'review' ||
        editor ||
        (decision === 'link' && (!canonical[proposal.proposalId] || proposal.strongConflict))
      )
        return;
      const current = generation.current;
      const changed = await apiPost<Workspace>(
        `${path}/${workspace.workspaceId}/relations/${proposal.proposalId}`,
        {
          decision,
          ...(decision === 'link' ? { canonicalRowId: canonical[proposal.proposalId] } : {}),
        },
      );
      if (generation.current !== current) return;
      setWorkspace(changed);
      setRelationId(null);
      setReviewedPacks([]);
      setPreview(null);
      setAcknowledged(false);
    });
  }
  function inspect() {
    void perform(async () => {
      if (!workspace || workspace.status !== 'review' || editor) return;
      const current = generation.current;
      const checked = await apiPost<Preview>(`${path}/${workspace.workspaceId}/preview`);
      if (generation.current !== current) return;
      setPreview(checked);
      setAcknowledged(false);
    });
  }
  function edit(row: StagedRow) {
    if (busy || workspace?.status === 'complete') return;
    setError(null);
    setSaveProgress(null);
    setEditor({ kind: 'row', rowId: row.rowId });
  }
  function revalidate() {
    void perform(async () => {
      if (!workspace || editor || workspace.status === 'complete') return;
      const current = generation.current;
      const next = await apiPost<Workspace>(`${path}/${workspace.workspaceId}/revalidate`, {
        expectedRevision: workspace.revision,
      });
      if (generation.current !== current) return;
      setWorkspace(next);
      setConfirmRevalidation(false);
      setPreview(null);
      setCanonical({});
      setReviewedPacks([]);
      setAcknowledged(false);
      setAnalysisNotice(null);
      setPanel('data');
    });
  }
  function saveCorrections(changes: readonly { rowId: string; values: Record<string, string> }[]) {
    void perform(async () => {
      if (!workspace || !editor || workspace.status === 'complete' || !changes.length) return;
      const current = generation.current;
      let updated = workspace;
      let saved = 0;
      setSaveProgress({ done: 0, total: changes.length });
      // Each row is an audited backend correction, carrying the preceding returned revision.
      // Successful rows remain saved if a later correction is rejected.
      for (const change of changes) {
        if (generation.current !== current) return;
        const currentRow = updated.rows.find((row) => row.rowId === change.rowId);
        if (!currentRow) throw new Error('ردیف منبع پیدا نشد؛ آخرین وضعیت را دریافت کنید.');
        const changedValues = Object.fromEntries(
          Object.entries(change.values).filter(
            ([field, value]) => String(currentRow.values[field] ?? '') !== value,
          ),
        );
        if (!Object.keys(changedValues).length) {
          saved++;
          setSaveProgress({ done: saved, total: changes.length });
          continue;
        }
        updated = await apiPost<Workspace>(
          path +
            '/' +
            encodeURIComponent(updated.workspaceId) +
            '/rows/' +
            encodeURIComponent(change.rowId),
          { expectedRevision: updated.revision, values: changedValues },
          { signal: requestController.current?.signal ?? null },
        );
        if (generation.current !== current) return;
        setWorkspace(updated);
        saved++;
        setSaveProgress({ done: saved, total: changes.length });
        setPreview(null);
        setCanonical({});
        setAcknowledged(false);
        setReviewedPacks([]);
        setAnalysisNotice(null);
      }
      setEditor(null);
      setStep(2);
    });
  }
  function reloadWorkspace() {
    void perform(async () => {
      if (!workspace) return;
      const current = generation.current;
      const latest = await apiGet<Workspace>(
        `${path}/${encodeURIComponent(workspace.workspaceId)}`,
        { signal: requestController.current?.signal ?? null },
      );
      if (generation.current !== current) return;
      setWorkspace(latest);
      setEditor(null);
      setPreview(null);
      setCanonical({});
      setAcknowledged(false);
      setAnalysisNotice(latest.analysisNotice ?? null);
      if (latest.status === 'complete') {
        setOutcome(latest.result ?? null);
        setStep(3);
        if (!latest.result) setError('این ورود قبلاً تکمیل شده، اما نتیجهٔ ثبت در دسترس نیست.');
      } else setStep(2);
    });
  }
  function reanalyze() {
    void perform(async () => {
      if (!workspace || editor || workspace.status === 'complete') return;
      const current = generation.current;
      const analyzed = await apiPost<Workspace>(
        `${path}/${encodeURIComponent(workspace.workspaceId)}/analyze`,
        undefined,
        { signal: requestController.current?.signal ?? null },
      );
      if (generation.current !== current) return;
      setWorkspace(analyzed);
      setPreview(null);
      setCanonical({});
      setAcknowledged(false);
      setAnalysisNotice(analyzed.analysisNotice ?? null);
      setStep(2);
    });
  }
  function continueAnalysis() {
    void perform(async () => {
      if (!workspace || editor || !importAnalysisPresentation(workspace, !!preview).continuable)
        return;
      const current = generation.current;
      let latest = workspace;
      stopOperation.current = false;
      setRelationProgress(null);
      setAnalysisRunning(true);
      try {
        while (!stopOperation.current && generation.current === current) {
          const before = importAnalysisPresentation(latest, false).processedPairs ?? 0;
          const changed = await apiPost<Workspace>(
            `${path}/${encodeURIComponent(latest.workspaceId)}/analyze-next`,
            { expectedRevision: latest.revision },
            { signal: requestController.current?.signal ?? null },
          );
          if (generation.current !== current) return;
          latest = changed;
          setWorkspace(changed);
          setPreview(null);
          setCanonical({});
          setAcknowledged(false);
          setAnalysisNotice(changed.analysisNotice ?? null);
          setPanel('relations');
          setStep(2);
          const next = importAnalysisPresentation(changed, false);
          if (
            !next.continuable ||
            changed.analysis?.status === 'unavailable' ||
            (next.processedPairs ?? 0) <= before
          )
            break;
        }
      } finally {
        if (generation.current === current) setAnalysisRunning(false);
      }
    });
  }
  async function decideMany(items: readonly RelationDecisionRequest[]): Promise<boolean> {
    let finished = false;
    const ok = await perform(async () => {
      if (!workspace || workspace.status !== 'review' || editor || !items.length) return;
      const current = generation.current;
      let latest = workspace;
      const pending = [...new Map(items.map((item) => [item.proposalId, item])).values()].filter(
        (item) =>
          latest.proposals.some(
            (p) => p.proposalId === item.proposalId && p.decision === 'pending',
          ),
      );
      const total = pending.length;
      let done = 0;
      stopOperation.current = false;
      setRelationProgress({ done, total });
      while (pending.length && !stopOperation.current && generation.current === current) {
        // Grow from the chosen primary record. Never send a primary outside the
        // pair's already-linked component; the server still enforces the contract.
        const index = pending.findIndex((item) => {
          if (item.decision === 'separate') return true;
          if (!item.canonicalRowId) return false;
          const connected = new Set([item.canonicalRowId]);
          let grew = true;
          while (grew) {
            grew = false;
            for (const p of latest.proposals) {
              if (
                p.decision !== 'link' ||
                (!connected.has(p.leftRowId) && !connected.has(p.rightRowId))
              )
                continue;
              for (const id of [p.leftRowId, p.rightRowId])
                if (!connected.has(id)) {
                  connected.add(id);
                  grew = true;
                }
            }
          }
          const p = latest.proposals.find((p) => p.proposalId === item.proposalId);
          return (
            !!p && !p.strongConflict && (connected.has(p.leftRowId) || connected.has(p.rightRowId))
          );
        });
        if (index < 0)
          throw new Error(
            'ارتباط‌های باقی‌مانده را تک‌به‌تک بررسی کنید؛ منبع اصلی به این بخش از گروه متصل نیست.',
          );
        const [item] = pending.splice(index, 1);
        const changed = await apiPost<Workspace>(
          `${path}/${latest.workspaceId}/relations/${item!.proposalId}`,
          {
            decision: item!.decision,
            ...(item!.decision === 'link' ? { canonicalRowId: item!.canonicalRowId } : {}),
          },
          { signal: requestController.current?.signal ?? null },
        );
        if (generation.current !== current) return;
        latest = changed;
        done++;
        setWorkspace(changed);
        setRelationProgress({ done, total });
        setReviewedPacks([]);
        setPreview(null);
        setAcknowledged(false);
      }
      finished = pending.length === 0 && generation.current === current;
      if (finished) setRelationProgress(null);
    });
    return !!ok && finished;
  }
  function discardPreviewForAnalysis() {
    void perform(async () => {
      if (!workspace || editor || workspace.status !== 'review' || workspace.result) return;
      const current = generation.current;
      const updated = await apiPost<Workspace>(
        `${path}/${encodeURIComponent(workspace.workspaceId)}/preview/discard`,
        { expectedRevision: workspace.revision },
        { signal: requestController.current?.signal ?? null },
      );
      if (generation.current !== current) return;
      setWorkspace(updated);
      setPreview(null);
      setCanonical({});
      setAcknowledged(false);
      setAnalysisNotice(updated.analysisNotice ?? null);
      setPanel('relations');
      setStep(2);
    });
  }
  function commit() {
    void perform(async () => {
      if (
        !workspace ||
        !allReviewed ||
        workspace.status !== 'review' ||
        !!editor ||
        !preview ||
        preview.errorCount ||
        !preview.committableCount ||
        (preview.warningCount && !acknowledged)
      )
        return;
      const current = generation.current;
      const result = await apiPost<Outcome>(`${path}/${workspace.workspaceId}/commit`, {
        previewToken: preview.previewToken,
        acknowledgedErrorCount: preview.errorCount,
        acknowledgedWarningCount: preview.warningCount,
      });
      if (generation.current !== current) return;
      setOutcome(result);
      setRowPage(0);
      setStep(3);
    });
  }
  useEffect(() => {
    setReviewedPacks([]);
  }, [workspace?.revision]);
  const review = workspace
    ? buildImportReview({ ...workspace, result: outcome ?? workspace.result ?? null }, packs)
    : null;
  const relationRows = (workspace?.rows ?? []).map((row) => {
    const columns =
      packs.find((pack) => pack.id === row.packId)?.sheets.find((sheet) => sheet.id === row.sheetId)
        ?.columns ?? [];
    return {
      ...row,
      values: Object.fromEntries(columns.map((column) => [column.key, row.values[column.key]])),
    };
  });
  const allReviewed =
    !!review?.sections.length &&
    review.sections.every((part) => reviewedPacks.includes(part.packId));
  const resultEvidence = !!review?.reconciliation.available;
  const analysisState = workspace ? importAnalysisPresentation(workspace, !!preview) : null;
  const openingCount = outcome?.opening === null ? 0 : (outcome?.opening?.itemCount ?? null);
  const nf = (value: number) => value.toLocaleString('fa-IR');
  const percent = (value: number | null) =>
    value === null ? 'گزارش نشده' : nf(Math.round(value * 100)) + '٪';
  const sheetFor = (row: StagedRow) =>
    packs.find((p) => p.id === row.packId)?.sheets.find((s) => s.id === row.sheetId);
  const rowName = (id: string) => {
    const row = workspace?.rows.find((r) => r.rowId === id);
    if (!row) return 'ردیف منبع';
    const visible = new Set(sheetFor(row)?.columns.map((c) => c.key) ?? []);
    const name = ['name', 'code', 'invoiceCode', 'orderCode'].find(
      (key) => visible.has(key) && row.values[key],
    );
    return String(name ? row.values[name] : 'ردیف ' + nf(row.sourceRow));
  };
  const selectPack = (id: string) => {
    setActivePack(id);
    setSheetId('');
    setRowPage(0);
    setIntegrationPage(0);
    setQuery('');
  };
  const scopedRows =
    workspace?.rows.filter((r) => activePack === 'all' || r.packId === activePack) ?? [];
  const availableSheets = packs.flatMap((p) =>
    p.sheets
      .filter((s) => scopedRows.some((r) => r.sheetId === s.id))
      .map((s) => ({ ...s, packId: p.id, packLabel: p.label })),
  );
  const selectedSheet = availableSheets.find((s) => s.id === sheetId);
  const rows = scopedRows.filter(
    (r) =>
      (!sheetId || r.sheetId === sheetId) &&
      (!onlyErrors || r.errors.length > 0) &&
      (!query ||
        (sheetFor(r)?.columns ?? []).some((c) =>
          String(r.values[c.key] ?? '')
            .toLocaleLowerCase()
            .includes(query.toLocaleLowerCase()),
        ) ||
        String(r.sourceRow) === query),
  );
  const pageSize = 20;
  const pageCount = Math.max(1, Math.ceil(rows.length / pageSize));
  const page = Math.min(rowPage, pageCount - 1);
  const currentRelation = workspace?.proposals.find((p) => p.proposalId === relationId);
  const currentScore = currentRelation ? relationConfidence(currentRelation) : null;
  const sourceRow = workspace?.rows.find((r) => r.rowId === sourceId);
  const selectedPart = review?.sections.find((s) => s.packId === activePack);
  const destinations = (outcome?.destinations ?? []).filter((d) =>
    scopedRows.some((r) => r.rowId === d.sourceRowId),
  );
  const destinationPageCount = Math.max(1, Math.ceil(destinations.length / pageSize));
  const destinationPage = Math.min(rowPage, destinationPageCount - 1);
  const links = (workspace?.referenceLinks ?? []).filter((link) =>
    scopedRows.some((row) => row.rowId === link.fromRowId),
  );
  const linkPageCount = Math.max(1, Math.ceil(links.length / pageSize));
  const linkPage = Math.min(integrationPage, linkPageCount - 1);
  const issueText = (row: StagedRow) =>
    row.errors
      .map((e) =>
        typeof e === 'string'
          ? e
          : (sheetFor(row)?.columns.find((c) => c.key === e.field)?.label ?? 'ردیف') +
            ': ' +
            e.message,
      )
      .join('؛ ');
  const pager = (
    current: number,
    total: number,
    count: number,
    change: (page: number) => void,
    name: string,
  ) => (
    <nav className="import-review__pager" aria-label={name}>
      <span>
        {nf(count)} مورد · صفحهٔ {nf(current + 1)} از {nf(total)}
      </span>
      <div>
        <V2Button
          variant="secondary"
          size="sm"
          disabled={current === 0 || busy}
          onClick={() => change(current - 1)}
        >
          قبلی
        </V2Button>
        <V2Button
          variant="secondary"
          size="sm"
          disabled={current + 1 >= total || busy}
          onClick={() => change(current + 1)}
        >
          بعدی
        </V2Button>
      </div>
    </nav>
  );
  const scoreBadge = (proposal: Relation) => {
    const score = relationConfidence(proposal);
    return (
      <V2Badge
        tone={
          score.bucket === 'high'
            ? 'success'
            : score.bucket === 'medium'
              ? 'warn'
              : score.bucket === 'low'
                ? 'danger'
                : 'neutral'
        }
      >
        {score.source === 'smart' ? 'اطمینان تحلیل: ' + percent(score.confidence) : score.label}
      </V2Badge>
    );
  };
  const rowColumns: V2TableColumn<StagedRow & { id: string }>[] = [
    {
      key: 'source',
      header: 'منبع',
      render: (r) => (
        <span className="import-review__source">
          <b>{label(r.packId)}</b>
          <small>
            {sheetFor(r)?.name} · ردیف {nf(r.sourceRow)}
          </small>
        </span>
      ),
    },
    ...(selectedSheet
      ? selectedSheet.columns.map((c) => ({
          key: c.key,
          header: (
            <span className="import-review__column-head">
              <span>{c.label}</span>
              <V2Button
                variant="ghost"
                size="sm"
                disabled={busy || !!editor || workspace?.status === 'complete'}
                onClick={() => {
                  setError(null);
                  setSaveProgress(null);
                  setEditor({
                    kind: 'column',
                    packId: selectedSheet.packId,
                    sheetId: selectedSheet.id,
                    columnKey: c.key,
                  });
                }}
              >
                ویرایش ستون {c.label}
              </V2Button>
            </span>
          ),
          render: (r: StagedRow) => (
            <span className="import-review__value">{String(r.values[c.key] ?? '—')}</span>
          ),
        }))
      : [
          {
            key: 'name',
            header: 'دادهٔ شناسایی‌شده',
            render: (r: StagedRow) => <b>{rowName(r.rowId)}</b>,
          },
          {
            key: 'target',
            header: 'خروجی مورد انتظار',
            render: (r: StagedRow) =>
              review?.sections
                .find((p) => p.packId === r.packId)
                ?.sheets.find((s) => s.sheetId === r.sheetId)?.targetLabel ?? 'نیازمند بررسی',
          },
        ]),
    {
      key: 'state',
      header: 'اعتبارسنجی',
      render: (r) =>
        r.errors.length ? (
          <span className="import-review__issue">
            <V2Badge tone="danger">{nf(r.errors.length)} خطا</V2Badge>
            <small>{issueText(r)}</small>
          </span>
        ) : (
          <V2Badge tone="success">ساختار معتبر</V2Badge>
        ),
    },
    {
      key: 'edit',
      header: 'عملیات',
      render: (r) => (
        <V2Button
          variant="secondary"
          size="sm"
          disabled={busy || !!editor || workspace?.status === 'complete'}
          onClick={() => edit(r)}
        >
          اصلاح ردیف {nf(r.sourceRow)}
        </V2Button>
      ),
    },
  ];
  return (
    <div className="import-review">
      <header className="import-review__heading">
        <div>
          <p className="import-review__eyebrow">ورود اطلاعات سازمان</p>
          <h2>
            {step === 0
              ? 'اطلاعات شما، در یک جریان یکپارچه'
              : step === 3
                ? 'گزارش ثبت و محل دسترسی به اطلاعات'
                : 'بازبینی و یکپارچه‌سازی اطلاعات'}
          </h2>
          <p>
            {step === 0
              ? 'قالب هر بخش را دریافت و فایل‌های تکمیل‌شده را انتخاب کنید.'
              : 'هر بخش را بررسی کنید؛ دادهٔ منبع، ارتباط‌ها و خروجی ثبت در دسترس شماست.'}
          </p>
        </div>
        <div className="import-review__heading-actions">
          <ImportGuide
            currentStep={step === 3 ? 5 : step === 2 ? 3 : step === 1 ? 2 : 0}
            maxRows={maxRows}
          />
          <V2Button variant="ghost" disabled={busy} onClick={onBack}>
            بازگشت
          </V2Button>
        </div>
      </header>
      <ol className="import-review__steps" aria-label="مراحل ورود اطلاعات">
        {steps.map((text, i) => (
          <li key={text} aria-current={step === i ? 'step' : undefined}>
            <span className={i <= step ? 'is-active' : ''}>{nf(i + 1)}</span>
            {text}
          </li>
        ))}
      </ol>
      {error ? (
        <Alert tone="danger" title="عملیات کامل نشد">
          {error}
          {editor
            ? ' ردیف‌هایی که پیش‌تر ذخیره شده‌اند حفظ می‌شوند؛ پیش از ادامه آخرین وضعیت را بررسی کنید.'
            : ''}
        </Alert>
      ) : null}
      {error && workspace && !editor ? (
        <V2Button variant="secondary" disabled={busy} onClick={reloadWorkspace}>
          دریافت آخرین وضعیت
        </V2Button>
      ) : null}
      {loading ? (
        <LoadingState />
      ) : step === 0 ? (
        <>
          <Alert tone="info">
            فقط بخش‌هایی را که اطلاعات دارید انتخاب کنید؛ لازم نیست برای همهٔ بخش‌ها فایل داشته
            باشید. هر فایل حداکثر ۵ و مجموع فایل‌ها حداکثر ۱۰ مگابایت؛ در هر مجموعه حداکثر{' '}
            {maxRows.toLocaleString('fa-IR')} ردیف داده بررسی می‌شود.
          </Alert>
          <div className="import-review__packs">
            {packs.map((pack) => (
              <ImportPackCard
                key={pack.id}
                pack={pack}
                file={files[pack.id]}
                busy={busy}
                onChoose={(file) => choose(pack.id, file)}
                onTemplate={() =>
                  void perform(async () => {
                    triggerBlobDownload(
                      await buildGuidedImportTemplate(pack, maxRows),
                      `${pack.id}-template.xlsx`,
                    );
                  })
                }
              />
            ))}
          </div>
          <div className="import-review__upload-summary">
            <div aria-live="polite">
              <strong>
                {Object.keys(files).length
                  ? `${nf(Object.keys(files).length)} فایل برای بررسی انتخاب شده`
                  : 'فایل بخش‌های موردنیاز را انتخاب کنید'}
              </strong>
              <small>
                {Object.keys(files).length
                  ? 'مرحلهٔ بعد: بررسی ردیف‌ها و ارتباط‌ها؛ ثبت نهایی پس از تأیید شما انجام می‌شود.'
                  : 'می‌توانید با فایل یک بخش شروع کنید؛ انتخاب همهٔ بخش‌ها ضروری نیست.'}
              </small>
            </div>
            <V2Button
              variant="primary"
              icon="upload"
              disabled={busy || !Object.keys(files).length}
              onClick={start}
            >
              {busy ? 'در حال آماده‌سازی…' : 'شروع ایمپورت و بررسی'}
            </V2Button>
          </div>
        </>
      ) : step === 1 ? (
        <V2Card>
          <V2CardHeader
            title="دریافت فایل‌ها انجام شد"
            subtitle="بررسی ساختار و پیشنهاد ارتباط‌ها در حال انجام است."
          />
          <div className="import-review__body">
            {busy ? <LoadingState /> : <V2Button onClick={reanalyze}>ادامهٔ بررسی</V2Button>}
          </div>
        </V2Card>
      ) : workspace && review ? (
        <>
          <div className="import-review__metrics">
            <V2MetricStrip>
              <V2Kpi
                label="ردیف‌های دریافت‌شده"
                value={nf(review.totalRows)}
                delta={nf(review.sections.length) + ' بخش اطلاعاتی'}
              />
              <V2Kpi
                label={outcome ? 'ردیف‌های دارای مقصد ثبت' : 'ردیف‌های با ساختار معتبر'}
                value={
                  outcome && !resultEvidence
                    ? '—'
                    : nf(outcome ? review.reconciliation.mappedSourceRows : review.validRows)
                }
                delta={
                  outcome
                    ? resultEvidence
                      ? 'قابل پیگیری در نتیجهٔ ثبت'
                      : 'گزارش مقصدها در دسترس نیست'
                    : 'اعتبارسنجی قالب؛ تأیید مدل محسوب نمی‌شود'
                }
              />
              <V2Kpi
                label={outcome ? 'طرف‌حساب، کالا، واحد و انبار جدید' : 'ردیف‌های نیازمند اصلاح'}
                value={
                  outcome && !resultEvidence
                    ? '—'
                    : nf(outcome ? review.outcome.mastersCreated : review.errorRows)
                }
                delta={
                  outcome
                    ? resultEvidence
                      ? nf(review.outcome.mastersExisting) + ' رکورد موجود استفاده شد'
                      : 'شمارش رکوردهای مقصد گزارش نشده'
                    : 'روی هر بخش برای اصلاح تمرکز کنید'
                }
              />
              <V2Kpi
                label={outcome ? 'سفارش‌های پیش‌نویس' : 'گروه‌ها و پیشنهادهای باز'}
                value={
                  outcome && !resultEvidence
                    ? outcome.orders
                      ? nf(outcome.orders.length)
                      : '—'
                    : nf(
                        outcome
                          ? review.outcome.draftOrders
                          : buildRelationReviewGroups(relationRows, workspace.proposals).filter(
                              (g) => g.pendingCount > 0,
                            ).length,
                      )
                }
                delta={
                  outcome
                    ? openingCount === null
                      ? 'گزارش افتتاحیه در دسترس نیست'
                      : nf(openingCount) + ' قلم افتتاحیهٔ پیش‌نویس'
                    : nf(review.conflicts) + ' تعارض هویتی'
                }
              />
            </V2MetricStrip>
          </div>
          <nav className="import-review__sections" aria-label="بخش‌های اطلاعات">
            <button
              type="button"
              className={
                'import-review__section import-review__section--all' +
                (activePack === 'all' ? ' is-selected' : '')
              }
              aria-pressed={activePack === 'all'}
              onClick={() => selectPack('all')}
            >
              <b>نمای کلی</b>
              <span>
                {nf(review.totalRows)} ردیف در {nf(review.sections.length)} بخش
              </span>
              <V2Badge tone={allReviewed ? 'success' : 'info'}>
                {outcome ? 'گزارش ثبت' : nf(reviewedPacks.length) + ' بخش بازبینی شده'}
              </V2Badge>
            </button>
            {review.sections.map((part) => (
              <button
                type="button"
                key={part.packId}
                data-module={importPackModule(part.packId)}
                className={
                  'import-review__section' + (activePack === part.packId ? ' is-selected' : '')
                }
                aria-pressed={activePack === part.packId}
                onClick={() => selectPack(part.packId)}
              >
                <div>
                  <b>{part.label}</b>
                  <V2Badge
                    tone={
                      outcome
                        ? part.reconciliation.available &&
                          part.reconciliation.unmappedSourceRows === 0
                          ? 'success'
                          : 'warn'
                        : part.errorRows
                          ? 'danger'
                          : part.pendingRelations
                            ? 'warn'
                            : reviewedPacks.includes(part.packId) || outcome
                              ? 'success'
                              : 'neutral'
                    }
                  >
                    {outcome
                      ? part.reconciliation.available &&
                        part.reconciliation.unmappedSourceRows === 0
                        ? 'منابع قابل پیگیری'
                        : 'نیازمند بررسی نتیجه'
                      : part.errorRows
                        ? nf(part.errorRows) + ' ردیف خطادار'
                        : part.pendingRelations
                          ? 'نیازمند بررسی ارتباط‌ها'
                          : reviewedPacks.includes(part.packId)
                            ? 'بازبینی شد'
                            : 'آمادهٔ بازبینی'}
                  </V2Badge>
                </div>
                <span>
                  {nf(part.sourceRows)} ردیف · {nf(part.sheets.length)} جدول
                </span>
                <small className="import-review__filename" title={part.fileName ?? 'فایل منبع'}>
                  {part.fileName ?? 'فایل منبع'}
                </small>
                <small>خروجی: {part.targetLabels.join('، ')}</small>
              </button>
            ))}
          </nav>
          {outcome ? (
            <V2Card>
              <V2CardHeader
                title="ثبت انجام شد؛ خروجی هر ردیف را ببینید"
                subtitle={
                  outcome.replayed
                    ? 'نتیجهٔ ذخیره‌شدهٔ اجرای قبلی بازیابی شد.'
                    : 'رکوردهای اصلی و پیش‌نویس‌ها از هم تفکیک شده‌اند.'
                }
              />
              <div className="import-review__body">
                {!resultEvidence ? (
                  <Alert tone="warning" title="گزارش مقصدها در دسترس نیست">
                    نتیجهٔ این ورود ذخیره شده، اما نگاشت ردیف‌های منبع به رکوردهای مقصد گزارش نشده
                    است. کامل‌بودن ثبت از این نتیجه قابل تأیید نیست.
                  </Alert>
                ) : (
                  <Alert tone={review.reconciliation.unmappedSourceRows ? 'warning' : 'success'}>
                    از {nf(review.totalRows)} ردیف منبع،{' '}
                    {nf(review.reconciliation.mappedSourceRows)} ردیف دارای مقصد ثبت است.
                    {review.reconciliation.unmappedSourceRows
                      ? ' ' +
                        nf(review.reconciliation.unmappedSourceRows) +
                        ' ردیف فاقد مقصد گزارش‌شده است؛ نتیجه را بررسی کنید.'
                      : ' تمام ردیف‌ها قابل پیگیری هستند.'}
                  </Alert>
                )}
                <div className="import-review__result-cards">
                  <div>
                    <b>{resultEvidence ? nf(review.outcome.mastersCreated) : '—'}</b>
                    <span>رکورد پایهٔ جدید</span>
                  </div>
                  <div>
                    <b>
                      {resultEvidence
                        ? nf(review.outcome.draftOrders)
                        : outcome.orders
                          ? nf(outcome.orders.length)
                          : '—'}
                    </b>
                    <span>سفارش پیش‌نویس</span>
                  </div>
                  <div>
                    <b>{openingCount === null ? '—' : nf(openingCount)}</b>
                    <span>قلم افتتاحیهٔ پیش‌نویس</span>
                  </div>
                  <div>
                    <b>
                      {outcome.retainedSettledInvoiceCount === undefined
                        ? '—'
                        : nf(outcome.retainedSettledInvoiceCount)}
                    </b>
                    <span>فاکتور تسویه‌شده؛ منبع حفظ شد</span>
                  </div>
                </div>
                {outcome.opening ? (
                  <Alert tone="info">
                    افتتاحیه با وضعیت پیش‌نویس ذخیره شده است. اثر قطعی مالی و موجودی پس از تطبیق و
                    تأیید مستقل ثبت می‌شود.
                  </Alert>
                ) : null}
                {outcome.opening?.batchId ? (
                  <a
                    className="import-review__primary-link"
                    href={base + '/data?opening=' + encodeURIComponent(outcome.opening.batchId)}
                  >
                    ادامهٔ تطبیق و تأیید افتتاحیه ←
                  </a>
                ) : null}
                <V2Table
                  columns={[
                    {
                      key: 'source',
                      header: 'ردیف منبع',
                      render: (d: ImportDestination & { id: string }) => (
                        <span className="import-review__source">
                          <b>{rowName(d.sourceRowId)}</b>
                          <small>
                            {label(
                              workspace.rows.find((r) => r.rowId === d.sourceRowId)?.packId ?? '',
                            )}{' '}
                            · ردیف{' '}
                            {nf(
                              workspace.rows.find((r) => r.rowId === d.sourceRowId)?.sourceRow ?? 0,
                            )}
                          </small>
                        </span>
                      ),
                    },
                    {
                      key: 'state',
                      header: 'نتیجه',
                      render: (d: ImportDestination & { id: string }) => (
                        <V2Badge tone={d.status === 'draft' ? 'info' : 'success'}>
                          {d.status === 'retained'
                            ? 'منبع تسویه‌شده حفظ شد'
                            : d.status === 'existing'
                              ? 'رکورد موجود'
                              : d.status === 'draft'
                                ? 'پیش‌نویس'
                                : 'ثبت شد'}
                        </V2Badge>
                      ),
                    },
                    {
                      key: 'target',
                      header: 'خروجی قابل مشاهده',
                      render: (d: ImportDestination & { id: string }) => {
                        const href = importDestinationPath(base, d);
                        return href ? (
                          <a href={href}>
                            {d.code ??
                              (d.kind === 'opening'
                                ? 'افتتاحیه'
                                : d.kind === 'source-evidence'
                                  ? 'پروندهٔ منبع'
                                  : 'مشاهدهٔ رکورد')}
                          </a>
                        ) : (
                          'مقصد گزارش نشده'
                        );
                      },
                    },
                    {
                      key: 'evidence',
                      header: 'جزئیات ردیف بازبینی‌شده',
                      render: (d: ImportDestination & { id: string }) => (
                        <V2Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setSourceId(d.sourceRowId)}
                        >
                          مشاهدهٔ اطلاعات
                        </V2Button>
                      ),
                    },
                  ]}
                  rows={destinations
                    .slice(destinationPage * pageSize, (destinationPage + 1) * pageSize)
                    .map((d, i) => ({ ...d, id: d.sourceRowId + ':' + i }))}
                />
                {pager(
                  destinationPage,
                  destinationPageCount,
                  destinations.length,
                  setRowPage,
                  'صفحه‌بندی نتیجهٔ ثبت',
                )}
                {(outcome.blockedPacks ?? []).map((p) => (
                  <Alert key={p.packId} tone="warning" title={label(p.packId)}>
                    {p.reason}
                  </Alert>
                ))}
              </div>
            </V2Card>
          ) : (
            <>
              {workspace.status === 'staged' ? (
                <Alert tone="warning" title="اطلاعات تغییر کرده است">
                  پس از اصلاح داده‌ها، پیشنهادها و پیش‌نمایش قبلی اعتبار ندارند.
                  <V2Button disabled={busy || !!editor} onClick={reanalyze}>
                    بررسی دوبارهٔ اطلاعات و ارتباط‌ها
                  </V2Button>
                </Alert>
              ) : null}
              {workspace.status === 'review' &&
              (preview || workspace.previewToken) &&
              (analysisState?.remainingPairs ?? 0) > 0 ? (
                <Alert tone="warning" title="هنوز ارتباط بررسی‌نشده وجود دارد">
                  <p>
                    {nf(analysisState?.remainingPairs ?? 0)} ارتباط باقی مانده است. برای ادامه،
                    پیش‌نمایش فعلی را حذف کنید؛ تصمیم‌های ثبت‌شدهٔ شما حفظ می‌شوند و پس از بررسی
                    می‌توانید پیش‌نمایش تازه بسازید.
                  </p>
                  <V2Button disabled={busy || !!editor} onClick={discardPreviewForAnalysis}>
                    حذف پیش‌نمایش و ادامهٔ بررسی
                  </V2Button>
                </Alert>
              ) : null}
              <V2Tabs
                activeId={panel}
                items={[
                  { id: 'data', label: '۱. داده‌ها و ستون‌ها' },
                  {
                    id: 'relations',
                    label: '۲. تکرارها و ارتباط‌ها',
                  },
                  { id: 'integration', label: '۳. مقصد داده‌ها' },
                  { id: 'commit', label: '۴. تأیید و ثبت' },
                ]}
                onChange={(id) => {
                  setPanel(id);
                  setRowPage(0);
                }}
              />
              {panel === 'data' ? (
                <V2Card>
                  <V2CardHeader
                    title={
                      selectedPart
                        ? 'داده‌های ' + selectedPart.label
                        : 'داده‌های استخراج‌شده از فایل‌ها'
                    }
                    subtitle="برای بررسی همهٔ ستون‌ها و ویرایش یک ستون، جدول منبع را انتخاب کنید."
                  />
                  <div className="import-review__body">
                    <V2Button
                      disabled={busy || !!editor}
                      onClick={() => setConfirmRevalidation(true)}
                    >
                      بازبینی خطاها با قواعد فعلی
                    </V2Button>
                    {confirmRevalidation ? (
                      <Alert tone="warning" title="بازبینی دوبارهٔ همهٔ ردیف‌ها">
                        <p>
                          مقدار سلول‌ها و فایل اصلی تغییر نمی‌کند. خطاها دوباره محاسبه می‌شوند؛
                          تصمیم‌های ارتباط و پیش‌نمایش قبلی پاک می‌شوند و باید دوباره بررسی شوند.
                        </p>
                        <V2Button disabled={busy || !!editor} onClick={revalidate}>
                          تأیید بازبینی خطاها
                        </V2Button>
                        <V2Button disabled={busy} onClick={() => setConfirmRevalidation(false)}>
                          انصراف از بازبینی
                        </V2Button>
                      </Alert>
                    ) : null}
                    <div className="import-review__toolbar">
                      <label>
                        جدول منبع
                        <select
                          className="sk-input"
                          value={sheetId}
                          onChange={(e) => {
                            setSheetId(e.target.value);
                            setRowPage(0);
                          }}
                        >
                          <option value="">همهٔ جدول‌ها</option>
                          {availableSheets.map((s) => (
                            <option key={s.id} value={s.id}>
                              {s.packLabel} · {s.name}
                            </option>
                          ))}
                        </select>
                      </label>
                      <label className="import-review__search">
                        جستجو در داده‌ها
                        <input
                          className="sk-input"
                          type="search"
                          value={query}
                          placeholder="نام، کد یا ردیف فایل…"
                          onChange={(e) => {
                            setQuery(e.target.value);
                            setRowPage(0);
                          }}
                        />
                      </label>
                      <label className="import-review__check">
                        <input
                          type="checkbox"
                          checked={onlyErrors}
                          onChange={(e) => {
                            setOnlyErrors(e.target.checked);
                            setRowPage(0);
                          }}
                        />
                        فقط ردیف‌های خطادار
                      </label>
                    </div>
                    {selectedSheet ? (
                      <div className="import-review__mapping-strip">
                        <V2Badge tone="info">قالب شناخته‌شده</V2Badge>
                        <span>
                          {selectedSheet.name} ←{' '}
                          {selectedPart?.sheets.find((s) => s.sheetId === sheetId)?.targetLabel ??
                            review.sections
                              .flatMap((s) => s.sheets)
                              .find((s) => s.sheetId === sheetId)?.targetLabel}
                        </span>
                        <small>
                          تطبیق ستون‌ها طبق قالب؛ اطمینان کلی JEV از این اطلاعات محاسبه نمی‌شود.
                        </small>
                      </div>
                    ) : null}
                    <div className="import-review__table">
                      <V2Table
                        columns={rowColumns}
                        rows={rows
                          .slice(page * pageSize, (page + 1) * pageSize)
                          .map((r) => ({ ...r, id: r.rowId }))}
                        emptyState={
                          <V2EmptyState
                            compact
                            title="ردیفی با این فیلتر پیدا نشد"
                            description="فیلتر خطا یا عبارت جستجو را تغییر دهید."
                          />
                        }
                      />
                    </div>
                    {pager(page, pageCount, rows.length, setRowPage, 'صفحه‌بندی داده‌های منبع')}
                  </div>
                </V2Card>
              ) : null}
              {panel === 'relations' ? (
                <GuidedRelationReview
                  key={workspace.workspaceId}
                  rows={relationRows}
                  proposals={workspace.proposals}
                  packs={packs}
                  files={workspace.files}
                  activePack={activePack}
                  busy={busy}
                  disabled={!!editor || workspace.status !== 'review'}
                  error={error}
                  saveProgress={relationProgress}
                  analysisRunning={analysisRunning}
                  analysis={workspace.analysis}
                  analysisNotice={analysisNotice}
                  hasPreview={!!preview || !!workspace.previewToken}
                  onAnalyze={analysisState?.continuable ? continueAnalysis : reanalyze}
                  onStop={() => {
                    stopOperation.current = true;
                  }}
                  onReviewPair={(id) => {
                    setError(null);
                    setRelationId(id);
                  }}
                  onDecide={decideMany}
                  onNext={() => setPanel('integration')}
                />
              ) : null}
              {panel === 'integration' ? (
                <V2Card>
                  <V2CardHeader
                    title="هر جدول به کدام بخش سیستم تبدیل می‌شود؟"
                    subtitle="فایل منبع، نوع خروجی و ارتباط‌های قابل بررسی کنار هم قرار گرفته‌اند."
                  />
                  <div className="import-review__body">
                    <V2Table
                      columns={[
                        {
                          key: 'source',
                          header: 'فایل و جدول مبدأ',
                          render: (s: {
                            id: string;
                            label: string;
                            pack: string;
                            targetLabel: string;
                            totalRows: number;
                            errorRows: number;
                          }) => (
                            <span className="import-review__source">
                              <b>{s.pack}</b>
                              <small>{s.label}</small>
                            </span>
                          ),
                        },
                        {
                          key: 'target',
                          header: 'خروجی در سیستم',
                          render: (s: { id: string; targetLabel: string }) => s.targetLabel,
                        },
                        {
                          key: 'rows',
                          header: 'ردیف منبع',
                          render: (s: { id: string; totalRows: number }) => nf(s.totalRows),
                        },
                        {
                          key: 'errors',
                          header: 'وضعیت',
                          render: (s: { id: string; errorRows: number }) => (
                            <V2Badge tone={s.errorRows ? 'danger' : 'success'}>
                              {s.errorRows
                                ? nf(s.errorRows) + ' ردیف نیازمند اصلاح'
                                : 'ساختار معتبر'}
                            </V2Badge>
                          ),
                        },
                      ]}
                      rows={review.sections
                        .filter((s) => activePack === 'all' || s.packId === activePack)
                        .flatMap((s) =>
                          s.sheets.map((t) => ({
                            ...t,
                            id: s.packId + ':' + t.sheetId,
                            pack: s.label,
                          })),
                        )}
                    />
                    <div className="import-review__mapping-strip">
                      <b>{nf(review.referenceLinks)} ارتباط بین اسناد و اطلاعات پایه</b>
                      <span>
                        {nf((workspace.groups ?? []).filter((g) => g.rowIds.length > 1).length)}{' '}
                        گروه طرف‌حساب یکپارچه‌شده
                      </span>
                    </div>
                    <details>
                      <summary>مشاهدهٔ ارتباط اسناد با مشتری، کالا، واحد و انبار</summary>
                      <div className="import-review__table">
                        <V2Table
                          columns={[
                            {
                              key: 'kind',
                              header: 'نوع ارتباط',
                              render: (l: { id: string; label: string }) => l.label,
                            },
                            {
                              key: 'from',
                              header: 'منبع سند',
                              render: (l: { id: string; fromRowId: string }) =>
                                rowName(l.fromRowId),
                            },
                            {
                              key: 'to',
                              header: 'مرتبط با',
                              render: (l: { id: string; toRowId: string }) => rowName(l.toRowId),
                            },
                          ]}
                          rows={links
                            .slice(linkPage * pageSize, (linkPage + 1) * pageSize)
                            .map((l, i) => ({ ...l, id: String(i) }))}
                        />
                      </div>
                      {pager(
                        linkPage,
                        linkPageCount,
                        links.length,
                        setIntegrationPage,
                        'صفحه‌بندی نقشهٔ ارتباط‌ها',
                      )}
                    </details>
                    <Alert tone="info">
                      این نقشه، پیش‌نویس مقصد را توضیح می‌دهد. شناسه و لینک رکورد واقعی پس از ثبت در
                      گزارش نتیجه نمایش داده می‌شود.
                    </Alert>
                  </div>
                </V2Card>
              ) : null}
              {panel === 'commit' ? (
                <V2Card>
                  <V2CardHeader
                    title="تأیید بخش‌ها و پیش‌نمایش ثبت"
                    subtitle="هر بخش را باز کنید و بررسی خود را تأیید کنید؛ سپس پیش‌نمایش سرور را دریافت کنید."
                  />
                  <div className="import-review__body">
                    <div className="import-review__checklist">
                      {review.sections.map((p) => (
                        <div key={p.packId}>
                          <span>
                            <b>{p.label}</b>
                            <small>
                              {nf(p.sourceRows)} ردیف · {p.targetLabels.join('، ')}
                            </small>
                          </span>
                          <label className="import-review__check">
                            <input
                              type="checkbox"
                              checked={reviewedPacks.includes(p.packId)}
                              disabled={
                                busy ||
                                p.errorRows > 0 ||
                                p.pendingRelations > 0 ||
                                workspace.status !== 'review'
                              }
                              onChange={(e) =>
                                setReviewedPacks((items) =>
                                  e.target.checked
                                    ? [...items, p.packId]
                                    : items.filter((id) => id !== p.packId),
                                )
                              }
                            />
                            {p.errorRows
                              ? 'ابتدا خطاها را اصلاح کنید'
                              : p.pendingRelations
                                ? 'ابتدا ارتباط‌ها را تعیین کنید'
                                : 'این بخش را بررسی کردم'}
                          </label>
                        </div>
                      ))}
                    </div>
                    <V2Button
                      disabled={
                        busy ||
                        !!editor ||
                        workspace.status !== 'review' ||
                        review.pendingRelations > 0 ||
                        review.errorRows > 0 ||
                        !allReviewed
                      }
                      onClick={inspect}
                    >
                      {busy ? 'در حال بررسی…' : 'دریافت پیش‌نمایش ثبت'}
                    </V2Button>
                    {preview ? (
                      <>
                        <V2MetricStrip>
                          <V2Kpi
                            label="قابل ثبت طبق پیش‌نمایش"
                            value={nf(preview.committableCount)}
                          />
                          <V2Kpi label="خطای مسدودکننده" value={nf(preview.errorCount)} />
                          <V2Kpi label="هشدار قابل بررسی" value={nf(preview.warningCount)} />
                        </V2MetricStrip>
                        {preview.diagnostics?.length ? (
                          <details open={preview.errorCount > 0}>
                            <summary>مشاهدهٔ {nf(preview.diagnostics.length)} پیام بررسی</summary>
                            <div className="import-review__diagnostics">
                              {preview.diagnostics.map((d, i) => (
                                <p key={d.code + ':' + i}>{d.message}</p>
                              ))}
                            </div>
                          </details>
                        ) : null}
                        {preview.blockedPacks.map((p) => (
                          <Alert key={p.packId} tone="warning" title={label(p.packId)}>
                            {p.reason}
                          </Alert>
                        ))}
                        {preview.errorCount ? (
                          <Alert tone="danger">
                            ثبت مسدود است؛ خطاهای پیش‌نمایش را اصلاح کنید.
                          </Alert>
                        ) : null}
                        {preview.warningCount ? (
                          <label className="import-review__check">
                            <input
                              type="checkbox"
                              checked={acknowledged}
                              disabled={busy}
                              onChange={(e) => setAcknowledged(e.target.checked)}
                            />
                            هشدارها و بخش‌های قابل ثبت را بررسی کردم.
                          </label>
                        ) : null}
                        <V2Button
                          disabled={
                            busy ||
                            !!editor ||
                            !allReviewed ||
                            !!preview.errorCount ||
                            !preview.committableCount ||
                            (!!preview.warningCount && !acknowledged)
                          }
                          onClick={commit}
                        >
                          تأیید و ثبت اطلاعات آماده
                        </V2Button>
                        <small className="import-review__muted">
                          ورود مالی و موجودی در این مرحله پیش‌نویس می‌سازد؛ ثبت قطعی افتتاحیه مسیر
                          تأیید جداگانه دارد.
                        </small>
                      </>
                    ) : null}
                  </div>
                </V2Card>
              ) : null}
              {selectedPart && panel !== 'commit' ? (
                <div className="import-review__section-footer">
                  <span>
                    {selectedPart.errorRows
                      ? nf(selectedPart.errorRows) + ' ردیف خطادار باقی مانده است.'
                      : selectedPart.pendingRelations
                        ? nf(selectedPart.pendingRelations) + ' ارتباط منتظر تصمیم است.'
                        : 'داده‌ها، ارتباط‌ها و مقصد این بخش را بررسی کردید؟'}
                  </span>
                  <V2Button
                    variant={reviewedPacks.includes(selectedPart.packId) ? 'secondary' : 'primary'}
                    disabled={
                      busy ||
                      workspace.status !== 'review' ||
                      selectedPart.errorRows > 0 ||
                      selectedPart.pendingRelations > 0 ||
                      reviewedPacks.includes(selectedPart.packId)
                    }
                    onClick={() => setReviewedPacks((items) => [...items, selectedPart.packId])}
                  >
                    {reviewedPacks.includes(selectedPart.packId)
                      ? 'این بخش بازبینی شد'
                      : 'تأیید بازبینی این بخش'}
                  </V2Button>
                </div>
              ) : null}
              <div className="import-review__actionbar">
                <span>
                  <b>{nf(review.errorRows)} ردیف خطادار</b> · {nf(review.pendingRelations)} تصمیم
                  باز · {nf(reviewedPacks.length)} از {nf(review.sections.length)} بخش بازبینی شده
                </span>
                <V2Button
                  variant="secondary"
                  disabled={busy || !!editor}
                  onClick={() =>
                    setPanel(
                      review.errorRows
                        ? 'data'
                        : review.pendingRelations
                          ? 'relations'
                          : panel === 'integration' || panel === 'commit'
                            ? 'commit'
                            : 'integration',
                    )
                  }
                >
                  {review.errorRows
                    ? 'اصلاح داده‌ها'
                    : review.pendingRelations
                      ? 'بررسی تکرارها و ارتباط‌ها'
                      : panel === 'integration' || panel === 'commit'
                        ? 'مرور نهایی و ثبت'
                        : 'ادامه به مقصد داده‌ها'}
                </V2Button>
              </div>
            </>
          )}
          <GuidedImportEditor
            editor={editor}
            rows={workspace.rows}
            packs={packs}
            busy={busy}
            error={error}
            saveProgress={saveProgress}
            onReload={reloadWorkspace}
            onClose={() => {
              if (!busy) {
                setEditor(null);
                setError(null);
              }
            }}
            onSave={saveCorrections}
          />
          <Modal
            size="lg"
            dismissible={!busy}
            open={!!currentRelation}
            title="بررسی ارتباط و انتخاب اطلاعات اصلی"
            onClose={() => {
              if (!busy) setRelationId(null);
            }}
            footer={
              currentRelation?.decision === 'pending' ? (
                <>
                  <V2Button
                    variant="secondary"
                    disabled={busy}
                    onClick={() => decide(currentRelation, 'separate')}
                  >
                    جدا نگه دار
                  </V2Button>
                  <V2Button
                    disabled={
                      busy ||
                      currentRelation.strongConflict ||
                      !canonical[currentRelation.proposalId]
                    }
                    onClick={() => decide(currentRelation, 'link')}
                  >
                    یک طرف‌حساب هستند
                  </V2Button>
                </>
              ) : (
                <V2Button variant="secondary" onClick={() => setRelationId(null)}>
                  بستن
                </V2Button>
              )
            }
          >
            {currentRelation && currentScore ? (
              <div className="import-review__relation-detail">
                {error ? <Alert tone="danger">{error}</Alert> : null}
                <div className="import-review__mapping-strip">
                  {scoreBadge(currentRelation)}
                  <span>احتمال یکسان‌بودن: {percent(currentScore.probability)}</span>
                </div>
                <p>{currentRelation.reason}</p>
                {currentRelation.strongConflict ? (
                  <Alert tone="danger">
                    شناسه‌های هویتی تعارض دارند؛ این دو طرف‌حساب قابل یکی‌کردن نیستند.
                  </Alert>
                ) : null}
                <div className="import-review__compare">
                  {[currentRelation.leftRowId, currentRelation.rightRowId].map((id) => {
                    const row = workspace.rows.find((r) => r.rowId === id);
                    if (!row) return null;
                    return (
                      <V2Card key={id}>
                        <V2CardHeader
                          title={rowName(id)}
                          subtitle={
                            label(row.packId) +
                            ' · ' +
                            sheetFor(row)?.name +
                            ' · ردیف ' +
                            nf(row.sourceRow)
                          }
                        />
                        <dl className="import-review__fields">
                          {sheetFor(row)
                            ?.columns.filter(
                              (c) => row.values[c.key] !== '' && row.values[c.key] != null,
                            )
                            .map((c) => (
                              <div key={c.key}>
                                <dt>{c.label}</dt>
                                <dd>{String(row.values[c.key])}</dd>
                              </div>
                            ))}
                        </dl>
                        {currentRelation.decision === 'pending' ? (
                          <label className="import-review__canonical">
                            <input
                              type="radio"
                              name="canonical-row"
                              value={id}
                              checked={canonical[currentRelation.proposalId] === id}
                              disabled={busy || currentRelation.strongConflict}
                              onChange={() =>
                                setCanonical((all) => ({
                                  ...all,
                                  [currentRelation.proposalId]: id,
                                }))
                              }
                            />
                            اطلاعات این ردیف، اطلاعات اصلی باشد
                          </label>
                        ) : null}
                      </V2Card>
                    );
                  })}
                </div>
                {currentRelation.decision === 'pending' ? (
                  <Alert tone="info">
                    مقادیر ردیف اصلی استفاده می‌شوند و سایر منبع‌ها حفظ می‌شوند. فیلدهای خالی ردیف
                    اصلی با اطلاعات منبع دیگر خودکار پر نمی‌شوند؛ ردیف کامل‌تر را انتخاب کنید.
                  </Alert>
                ) : (
                  <V2Badge tone="success">
                    {currentRelation.decision === 'link'
                      ? 'ارتباط تأیید شده است'
                      : 'این دو ردیف جدا نگه داشته شده‌اند'}
                  </V2Badge>
                )}
              </div>
            ) : null}
          </Modal>
          <Modal
            open={!!sourceRow}
            title="جزئیات ردیف بازبینی‌شده"
            onClose={() => setSourceId(null)}
          >
            {sourceRow ? (
              <>
                <p>
                  {label(sourceRow.packId)} · {sheetFor(sourceRow)?.name} · ردیف{' '}
                  {nf(sourceRow.sourceRow)}
                </p>
                <dl className="import-review__fields">
                  {sheetFor(sourceRow)?.columns.map((c) => (
                    <div key={c.key}>
                      <dt>{c.label}</dt>
                      <dd>{String(sourceRow.values[c.key] ?? '—')}</dd>
                    </div>
                  ))}
                </dl>
              </>
            ) : null}
          </Modal>
        </>
      ) : (
        <V2EmptyState
          title="نتیجهٔ ورود در دسترس نیست"
          description="آخرین وضعیت این ورود را دوباره دریافت کنید."
          action={<V2Button onClick={reloadWorkspace}>دریافت آخرین وضعیت</V2Button>}
        />
      )}
    </div>
  );
}
